import { CommonModule, Location, NgForOf } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import printJS from 'print-js';
import { TuiAlertService, TuiButton } from '@taiga-ui/core';
import { TuiTable } from '@taiga-ui/addon-table';
import { TuiCell } from '@taiga-ui/layout';
import { ProcedureRecordService } from '../../core/services/procedure-record.service';

/**
 * Walk-in single-record invoice.
 *
 * Walk-in clients have no Client document and no shifts. The existing
 * `/invoice/:clientId/:month/:year` page is built around a Client document
 * plus monthly shift aggregation, so it can't render walk-ins. This view
 * uses the same simple invoice format (logo, header band, billing block,
 * additional charges table, totals, account details) but is keyed by a
 * single ProcedureRecord and only renders the walk-in fields:
 *   - walkInClientName / walkInContactNo
 *   - procedures + payments breakdown
 *   - grandTotal
 * No shift table is rendered.
 */
@Component({
  selector: 'app-walk-in-invoice',
  standalone: true,
  imports: [CommonModule, NgForOf, TuiButton, TuiTable, TuiCell],
  templateUrl: './walk-in-invoice.component.html',
  styleUrl: './walk-in-invoice.component.scss',
})
export class WalkInInvoiceComponent implements OnInit {
  procedureId = '';
  record: any | null = null;
  isLoading = false;
  today = new Date();

  private route = inject(ActivatedRoute);
  private procedureService = inject(ProcedureRecordService);
  private alerts = inject(TuiAlertService);
  private location = inject(Location);

  ngOnInit(): void {
    this.procedureId = this.route.snapshot.paramMap.get('procedureId') || '';
    if (this.procedureId) {
      this.loadRecord();
    }
  }

  loadRecord(): void {
    this.isLoading = true;
    this.procedureService.getProcedureById(this.procedureId).subscribe({
      next: (data: any) => {
        this.record = data || null;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading walk-in record:', err);
        this.alerts
          .open('Error loading walk-in invoice', { appearance: 'error' })
          .subscribe();
        this.isLoading = false;
      },
    });
  }

  goBack(): void {
    this.location.back();
  }

  printInvoice(): void {
    if (!this.record) {
      return;
    }

    const logoUrl = `${window.location.origin}/assets/logo-vertical.png`;
    const todayStr = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const serviceDate = this.getServiceDate();
    const serviceDateStr = serviceDate
      ? serviceDate.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "—";

    const escapeHtml = (text: string): string => {
      if (!text) return "";
      return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    };

    const formatDate = (date: any): string => {
      if (!date) return "—";
      const d = new Date(date);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    };

    const nurseName = this.getNurseName();
    const doctorName = this.getDoctorName();
    const description = this.getProceduresDisplay();
    const grandTotalStr = this.getGrandTotal().toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    
    // Breakdown html
    const breakdownItems = this.getPaymentBreakdown();
    let breakdownHtml = "";
    if (breakdownItems && breakdownItems.length > 0) {
      const gridItems = breakdownItems
        .map((item) => `<div style="color: #64748b; font-size: 9px; font-weight: 500;">${escapeHtml(item.label)} : ${item.amount.toFixed(2)}</div>`)
        .join("");
      breakdownHtml = `
      <tr style="background-color: #f8fafc;">
        <td colspan="5" style="padding: 4px 12px;">
          <div style="font-size: 9px; font-weight: 700; color: #64748b; margin-bottom: 2px; text-transform: uppercase; letter-spacing: 0.04em;">Payment Breakdown</div>
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 2px 12px;">
            ${gridItems}
          </div>
        </td>
      </tr>`;
    }

    const rowHtml = `
      <tr>
        <td style="padding: 5px 12px; font-size: 11px; color: #1e293b;">${formatDate(serviceDate)}</td>
        <td style="padding: 5px 12px; font-size: 11px; color: #1e293b;">${escapeHtml(nurseName)}</td>
        <td style="padding: 5px 12px; font-size: 11px; color: #1e293b;">${escapeHtml(doctorName)}</td>
        <td style="padding: 5px 12px; font-size: 11px; color: #1e293b;">${escapeHtml(description)}</td>
        <td style="padding: 5px 12px; font-size: 11px; color: #2563eb; text-align: right; font-weight: 700;">LKR ${grandTotalStr}</td>
      </tr>
      ${breakdownHtml}
    `;

    const grandTotalFormatted = Math.round(this.getGrandTotal()).toLocaleString("en-US");

    const html = `
      <div style="font-family:'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#1e293b;padding:4px 4px 12px;line-height:1.25;display:flex;flex-direction:column;min-height:272mm;justify-content:space-between;box-sizing:border-box;">
        <div>
          <!-- Header -->
          <div style="display: flex; align-items: center; justify-content: space-between; position: relative; padding-bottom: 6px; border-bottom: 1px solid #cbd5e1;">
            <div style="width: 80px; display: flex; align-items: center; justify-content: flex-start;">
              <img src="${logoUrl}" alt="Royal Hospital Logo" style="height: 52px; width: auto; object-fit: contain;" />
            </div>
            <div style="flex: 1; text-align: center; line-height: 1.3;">
              <div style="font-size: 18px; font-weight: 800; color: #00b0ff; text-transform: uppercase; letter-spacing: 0.5px;">ROYAL HOSPITAL</div>
              <div style="font-size: 11px; font-weight: 800; color: #e91e63; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 1px;">HOME NURSING CARE</div>
              <div style="font-size: 9px; color: #475569; margin-top: 2px; font-weight: 500;">62 W A Silva Mawatha Colombo 06</div>
              <div style="font-size: 8px; color: #475569; margin-top: 1px; font-weight: 500;">Hot Line: 0713257499(WhatsApp), 0760656778, 0114000100</div>
            </div>
            <div style="width: 80px;"></div>
          </div>

          <!-- Metadata Section -->
          <div style="margin: 10px 0; font-size: 11px; display: grid; grid-template-columns: 110px 1fr auto 110px; gap: 4px 12px; line-height: 1.3;">
            <div style="color: #64748b;">Client Reg No.</div>
            <div style="font-weight: 700; color: #0f172a;">${escapeHtml(this.record?.client?.refNo || "Walk-in")}</div>
            <div style="color: #64748b; text-align: right;">Date</div>
            <div style="font-weight: 700; color: #0f172a; text-align: right;">${todayStr}</div>

            <div style="color: #64748b;">Invoice Number</div>
            <div style="font-weight: 700; color: #285ca5;">${this.getInvoiceNumber()}</div>
            <div style="color: #64748b; text-align: right;">Service Date</div>
            <div style="font-weight: 700; color: #0f172a; text-align: right;">${serviceDateStr}</div>

            <div style="color: #64748b;">Client Name</div>
            <div style="font-weight: 700; color: #0f172a; grid-column: span 3;">${escapeHtml(this.record?.walkInClientName || "Walk-in")}</div>

            <div style="color: #64748b;">Contact No</div>
            <div style="font-weight: 700; color: #0f172a; grid-column: span 3;">${escapeHtml(this.record?.walkInContactNo || "—")}</div>

            <div style="color: #64748b;">Address</div>
            <div style="font-weight: 700; color: #0f172a; grid-column: span 3;">${escapeHtml(this.record?.address || "—")}</div>
          </div>

          <!-- Charges Table -->
          <div style="font-size: 11px; font-weight: 700; color: #1e293b; margin: 8px 0 4px 0; text-transform: capitalize;">Charges</div>
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px;">
            <thead>
              <tr style="border-top: 1px solid #cbd5e1; border-bottom: 1px solid #cbd5e1; background-color: #f8fafc;">
                <th style="text-align: left; padding: 4px 12px; font-size: 10px; font-weight: 600; color: #475569; width: 15%;">Date</th>
                <th style="text-align: left; padding: 4px 12px; font-size: 10px; font-weight: 600; color: #475569; width: 20%;">Nurse Name</th>
                <th style="text-align: left; padding: 4px 12px; font-size: 10px; font-weight: 600; color: #475569; width: 15%;">Doctor Name</th>
                <th style="text-align: left; padding: 4px 12px; font-size: 10px; font-weight: 600; color: #475569; width: 35%;">Description / Procedure</th>
                <th style="text-align: right; padding: 4px 12px; font-size: 10px; font-weight: 600; color: #475569; width: 15%;">Shift Charges</th>
              </tr>
            </thead>
            <tbody style="border-bottom: 1px solid #cbd5e1;">
              ${rowHtml}
            </tbody>
          </table>

          <!-- Totals Section -->
          <div style="margin-top: 12px; display: flex; flex-direction: column; align-items: flex-end; font-size: 11px; color: #0f172a; line-height: 1.6;">
            <div style="display: grid; grid-template-columns: 180px 100px; text-align: right; gap: 2px 16px;">
              <div style="color: #475569; font-weight: 500; text-align: left;">Additional Charges (Net)</div>
              <div style="font-weight: 700; text-align: right;">${grandTotalFormatted}LKR</div>
              
              <div style="font-weight: 700; text-align: left; font-size: 12px;">Amount to be Paid</div>
              <div style="font-weight: 800; text-align: right; font-size: 12px;">${grandTotalFormatted}LKR</div>
            </div>
          </div>
        </div>

        <!-- Account Details Footer -->
        <div style="border-top: 1px solid #cbd5e1; padding-top: 10px; font-family: sans-serif; font-size: 11px; display: flex; gap: 40px; margin-top: auto;">
          <div style="font-weight: 700; color: #475569; width: 140px;">Account Details</div>
          <div style="color: #1e293b; line-height: 1.5;">
            Royal Hospital Home Nursing (pvt) ltd<br />
            Commercial Bank 1000650035<br />
            Wellawatta 02nd Branch<br />
            Swift Code CCEYLKLX
          </div>
        </div>
      </div>
    `;

    printJS({
      printable: html,
      type: "raw-html",
      documentTitle: `Invoice_${this.record.walkInClientName || "WalkIn"}_${this.getInvoiceNumber()}`,
      scanStyles: false,
      style: `
        @page { size: A4 portrait; margin: 8mm 8mm; }
        body { margin: 0; }
      `,
    });
  }

  getProceduresDisplay(): string {
    const procedures = this.record?.procedures;
    if (Array.isArray(procedures) && procedures.length > 0) {
      return procedures.join(', ');
    }
    return this.record?.procedure || 'N/A';
  }

  getGrandTotal(): number {
    return Number(this.record?.grandTotal || 0);
  }

  getPaymentBreakdown(): { label: string; amount: number }[] {
    const breakdown: { label: string; amount: number }[] = [];
    const payments = this.record?.payments || {};

    if (payments.doctorPayment > 0)
      breakdown.push({ label: 'Doctor', amount: payments.doctorPayment });
    if (payments.nursingPayment > 0)
      breakdown.push({ label: 'Nursing', amount: payments.nursingPayment });
    if (payments.pharmacyPayment > 0)
      breakdown.push({ label: 'Pharmacy', amount: payments.pharmacyPayment });
    if (payments.laboratoryPayment > 0)
      breakdown.push({ label: 'Laboratory', amount: payments.laboratoryPayment });
    if (payments.ambulancePayment > 0)
      breakdown.push({ label: 'Ambulance', amount: payments.ambulancePayment });
    if (payments.physiotherapyPayment > 0)
      breakdown.push({
        label: 'Physiotherapy',
        amount: payments.physiotherapyPayment,
      });
    if (payments.otherPayment > 0)
      breakdown.push({ label: 'Other', amount: payments.otherPayment });

    if (Array.isArray(this.record?.customPayments)) {
      for (const cp of this.record.customPayments) {
        if (cp?.value && Number(cp.value) > 0) {
          breakdown.push({ label: cp.name || 'Custom', amount: Number(cp.value) });
        }
      }
    }

    return breakdown;
  }

  getNurseName(): string {
    return this.record?.nurseName || this.record?.nurse?.name || 'Unavailable';
  }

  getDoctorName(): string {
    return this.record?.doctorName || 'Unavailable';
  }

  /**
   * Walk-in invoice number: WLK + last 6 of the procedure _id + YYYYMM.
   * Matches the format the walk-in report generates so the two stay in sync.
   */
  getInvoiceNumber(): string {
    const id = this.record?._id ? String(this.record._id) : '';
    if (!id) return 'Unavailable';
    const slug = id.slice(-6).toUpperCase();
    const date = this.record?.date ? new Date(this.record.date) : new Date();
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    return `WLK${slug}${y}${m}`;
  }

  getServiceDate(): Date | null {
    if (!this.record) return null;
    const raw = this.record.date || this.record.createdAt;
    return raw ? new Date(raw) : null;
  }
}
